import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Breadcrumbs } from '@vendasta/uikit';
import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  observeOn,
  shareReplay,
  startWith,
} from 'rxjs';
import { NavActionButton, NavigationData } from './navigation.interface';

function mergeData(route: ActivatedRoute, data: NavigationData = {}): NavigationData {
  if (route.snapshot?.data) {
    const { appUrl, injectedPageContent, pageOptions, breadcrumbs } = route.snapshot.data;

    data.pageOptions = { ...data.pageOptions, ...pageOptions };
    data.appUrl = appUrl;
    if (breadcrumbs) data.breadcrumbs = breadcrumbs;

    data.injectedPageContent = { ...data.injectedPageContent, ...injectedPageContent };
    data.title = data.title || route.snapshot.data.title;
  }

  route.children.forEach((childRoute) => mergeData(childRoute, data));

  return data;
}

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private actionButtons$$: BehaviorSubject<NavActionButton[]> = new BehaviorSubject<NavActionButton[]>([]);

  readonly routeData$: Observable<NavigationData> = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith(null),
    map(() => mergeData(this.route)),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  private readonly breadcrumbs$$ = new BehaviorSubject<Breadcrumbs[]>([]);
  public readonly breadcrumbs$ = combineLatest([
    this.routeData$.pipe(map((data) => data.breadcrumbs)),
    this.breadcrumbs$$.pipe(observeOn(asyncScheduler)),
  ]).pipe(
    map(([dataBreadcrumbs, breadcrumbs]) => {
      return dataBreadcrumbs?.length ? dataBreadcrumbs : breadcrumbs;
    }),
  );

  private readonly pageOptions$ = this.routeData$.pipe(map((data) => data.pageOptions));
  public readonly displayDateSelector$ = this.pageOptions$.pipe(map((opts) => opts.displayDateSelector));
  public readonly displayPrintButton$ = this.pageOptions$.pipe(map((opts) => opts.displayPrintButton));
  public readonly displayComposeButton$ = this.pageOptions$.pipe(map((opts) => opts.displayComposeButton));
  public readonly displayFilterButton$ = this.pageOptions$.pipe(map((opts) => opts.displayFilterButton));
  public readonly displayFilterContainer$ = this.pageOptions$.pipe(map((opts) => opts.displayFilterContainer));
  public readonly displayFullScreen$ = this.pageOptions$.pipe(map((opts) => opts.displayFullScreen));
  public readonly subtractToolbarOffset$ = this.pageOptions$.pipe(map((opts) => opts.subtractToolbarOffset));
  public readonly hideNavToolbar$ = this.pageOptions$.pipe(map((opts) => opts.hideNavToolbar));

  private readonly dataActionButtons$ = this.routeData$.pipe(map((data) => data.injectedPageContent?.actionButtons));
  public readonly actionButtons$ = combineLatest([this.dataActionButtons$, this.actionButtons$$]).pipe(
    map(([dataButtons, actionButtons]) => [...(dataButtons || []), ...actionButtons]),
  );

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        distinctUntilChanged((prev, curr) => prev['url'] === curr['url']),
      )
      .subscribe(() => this.actionButtons$$.next([]));
  }

  setBreadcrumbs(breadcrumbs: Breadcrumbs[]): void {
    this.breadcrumbs$$.next(breadcrumbs);
  }

  setActionButtons(actionButtons: NavActionButton[]): void {
    this.actionButtons$$.next(actionButtons);
  }
}
